import React, { useState, useEffect } from 'react';
import { PageSection, SectionContent, Language } from '../../../types/pageBuilder';
import FieldEditor from './FieldEditor';
import pageBuilderService from '../../../services/pageBuilderService';

interface SectionEditorProps {
  section: PageSection;
  language: Language;
  onClose: () => void;
  onSave: () => void;
}

/**
 * Modal editor for section content
 */
const SectionEditor: React.FC<SectionEditorProps> = ({
  section,
  language,
  onClose,
  onSave
}) => {
  const [content, setContent] = useState<SectionContent>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.id, language]);

  const loadContent = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pageBuilderService.getSectionContent(section.id, language);

      // Ensure data structure is correct - API returns {field_key: {value, type, id, updated_at}}
      // We need to keep this structure for SectionContent
      setContent(data as SectionContent);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldKey: string, value: string | any[]) => {
    setContent((prev) => ({
      ...prev,
      [fieldKey]: {
        ...prev[fieldKey],
        value: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Prepare fields for bulk update
      const fields: { [key: string]: string | any[] } = {};
      Object.entries(content).forEach(([key, field]) => {
        fields[key] = field.value;
      });

      await pageBuilderService.bulkUpdateContent(section.id, language, fields);
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const getFieldLabel = (fieldKey: string): string => {
    return fieldKey
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Edit Section: {section.section_type}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Language: {language.toUpperCase()} | Section Key: {section.section_key}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
              {error}
            </div>
          ) : Object.keys(content).length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No content fields found for this section.</p>
              <p className="text-sm mt-2">Content may need to be initialized in the database.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(content).map(([fieldKey, field]) => {
                // ContentField always has {id, value, type, updated_at} structure
                // We need to extract the actual value from field.value

                // Safety: Ensure field has the expected structure
                if (!field || typeof field !== 'object') {
                  return null;
                }

                // Ensure we have both value and type
                const fieldValue = field.value !== undefined ? field.value : '';
                const fieldType = field.type || 'text';

                return (
                  <FieldEditor
                    key={fieldKey}
                    fieldKey={fieldKey}
                    fieldValue={fieldValue}
                    fieldType={fieldType}
                    label={getFieldLabel(fieldKey)}
                    onChange={(value) => handleFieldChange(fieldKey, value)}
                    sectionId={section.id}
                    language={language}
                    onItemsChange={loadContent}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading || Object.keys(content).length === 0}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              saving || loading || Object.keys(content).length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {saving ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Saving...
              </span>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SectionEditor;
